import config, { buildCategoryCoverUrl } from '../../../config';

export const YOUR_PROGRESS_PATH = '/dashboard/tracks/your-progress';

export function sessionPath(contentId) {
  const id = String(contentId ?? '').trim();
  return id ? `${YOUR_PROGRESS_PATH}/${encodeURIComponent(id)}` : YOUR_PROGRESS_PATH;
}

export function formatClock(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

export function actionEntries(content) {
  if (!Array.isArray(content?.actions)) return [];
  return content.actions.flatMap((item, index) => {
    const title = String(item ?? '').trim();
    return title ? [{ index, title }] : [];
  });
}

const PLAYBACK_STARTED_PREFIX = 'richuelPlaybackStarted';

function playbackStartedKey() {
  if (typeof localStorage === 'undefined') return PLAYBACK_STARTED_PREFIX;
  const email = String(localStorage.getItem(config.STORAGE_KEYS.EMAIL) || '').trim().toLowerCase();
  return email ? `${PLAYBACK_STARTED_PREFIX}:${email}` : PLAYBACK_STARTED_PREFIX;
}

export function readPlaybackStartedIds() {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = JSON.parse(localStorage.getItem(playbackStartedKey()) || '[]');
    return new Set((Array.isArray(raw) ? raw : []).map((id) => String(id)));
  } catch {
    return new Set();
  }
}

export function markPlaybackStarted(contentId) {
  const id = String(contentId ?? '').trim();
  if (!id || typeof localStorage === 'undefined') return;
  const ids = readPlaybackStartedIds();
  if (ids.has(id)) return;
  ids.add(id);
  try {
    localStorage.setItem(playbackStartedKey(), JSON.stringify([...ids]));
  } catch {
    // Playback still counts for this visit if storage is unavailable.
  }
}

export function contentProgressValue(content, { playbackStarted = false } = {}) {
  const actions = actionEntries(content);
  const valid = new Set(actions.map((item) => item.index));
  const checked = new Set(
    (Array.isArray(content?.checked_actions) ? content.checked_actions : [])
      .map((index) => Number(index))
      .filter((index) => valid.has(index)),
  );
  const audioDone = Boolean(content?.video_watched);
  const hasActions = actions.length > 0;
  // Listening is half of a Quick Richuel. Action items share the other half.
  // A listen-only Richuel is 100% once the audio is finished.
  const audioScore = audioDone ? (hasActions ? 50 : 100) : 0;
  const actionScore = hasActions ? (50 * checked.size) / actions.length : 0;
  const progress = Math.round(audioScore + actionScore);
  const complete = audioDone && (!hasActions || checked.size === actions.length);
  // Opening a Quick Richuel is exploration. It becomes in progress only after
  // the listener presses play or checks an action item.
  const started = audioDone || checked.size > 0 || Boolean(playbackStarted);
  let status = 'not_started';
  if (complete) status = 'completed';
  else if (started) status = 'in_progress';
  return { progress, status, actions, checked };
}

function contentsByCategory(contents) {
  const byCategory = new Map();
  (Array.isArray(contents) ? contents : []).forEach((content) => {
    const categoryId = String(content?.category_id ?? '');
    if (!byCategory.has(categoryId)) byCategory.set(categoryId, []);
    byCategory.get(categoryId).push(content);
  });
  return byCategory;
}

function contentSteps(contents, playbackStartedIds) {
  const startedIds = playbackStartedIds instanceof Set ? playbackStartedIds : new Set();
  return (Array.isArray(contents) ? contents : []).map((content) => {
    const contentId = String(content?.id ?? '');
    const { progress, status, actions, checked } = contentProgressValue(content, {
      playbackStarted: startedIds.has(contentId),
    });
    return {
      id: contentId,
      title: String(content?.title || 'Untitled').trim() || 'Untitled',
      status,
      progress,
      actionItems: actions.map((item) => ({
        id: `${contentId}-${item.index}`,
        title: item.title,
        status: checked.has(item.index) ? 'completed' : 'not_started',
      })),
    };
  });
}

function sessionFromSteps(category, steps, status, sessionId) {
  const id = String(category?.id ?? '');
  const name = String(category?.name || category?.category || 'Category').trim() || 'Category';
  const progress = steps.length
    ? Math.round(steps.reduce((sum, step) => sum + step.progress, 0) / steps.length)
    : 0;
  const openContentId = (steps.find((step) => step.status !== 'completed') || steps[0])?.id || '';
  return {
    id: sessionId,
    title: name,
    category: name,
    coverSrc: buildCategoryCoverUrl({
      categoryId: id,
      filename: category?.cover_image,
    }) || '',
    durationLabel: `${steps.length} ${steps.length === 1 ? 'Quick Richuel' : 'Quick Richuels'}`,
    progress,
    status,
    openContentId,
    actions: steps,
  };
}

function pushBoardSessions(category, steps, inProgress, completed) {
  const id = String(category?.id ?? '');
  const remaining = steps.filter((step) => step.status !== 'completed');
  const done = steps.filter((step) => step.status === 'completed');
  if (remaining.length) {
    inProgress.push(sessionFromSteps(category, remaining, 'in_progress', `${id}:in_progress`));
  }
  if (done.length) completed.push(sessionFromSteps(category, done, 'completed', `${id}:completed`));
}

/**
 * Finished contents move to Completed.
 * Every other content in the list stays under In Progress.
 * Your Progress loads this list with not-started contents already omitted.
 */
export function buildProgressBoards(categories, contents, playbackStartedIds) {
  const byCategory = contentsByCategory(contents);
  const inProgress = [];
  const completed = [];
  const seen = new Set();
  (Array.isArray(categories) ? categories : []).forEach((category) => {
    const id = String(category?.id ?? '');
    seen.add(id);
    pushBoardSessions(category, contentSteps(byCategory.get(id) || [], playbackStartedIds), inProgress, completed);
  });
  // Contents whose category was not in the category list still need a row.
  byCategory.forEach((items, id) => {
    if (seen.has(id)) return;
    const name = String(items[0]?.category || 'Category').trim() || 'Category';
    pushBoardSessions(
      { id, name, category: name },
      contentSteps(items, playbackStartedIds),
      inProgress,
      completed,
    );
  });
  return { inProgress, completed };
}

export function summarizeContents(contents) {
  const list = Array.isArray(contents) ? contents : [];
  const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
  let watched = 0;
  let actionsCompleted = 0;
  let completed = 0;
  let completedThisWeek = 0;
  list.forEach((content) => {
    const { status, checked } = contentProgressValue(content);
    if (content?.video_watched) watched += 1;
    actionsCompleted += checked.size;
    if (status !== 'completed') return;
    completed += 1;
    const updated = Date.parse(content?.updated_at || '');
    if (Number.isFinite(updated) && updated >= weekAgo) completedThisWeek += 1;
  });
  return {
    watched,
    actionsCompleted,
    completed,
    completedThisWeek,
    total: list.length,
  };
}

export function nextContent(contents, preferredCategoryName) {
  const list = Array.isArray(contents) ? contents : [];
  const incomplete = list.filter((content) => contentProgressValue(content).status !== 'completed');
  const preferred = String(preferredCategoryName || '').trim();
  if (preferred) {
    const match = incomplete.find((content) => String(content?.category || '').trim() === preferred);
    if (match) return match;
  }
  return incomplete[0] || list[0] || null;
}
